import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { produce } from 'immer'
import {
  Card,
  CardActions,
  CardBody,
  CardHeader,
  CardTitle,
  GridLayout,
  GridLayoutItem,
  StackLayout
} from '@progress/kendo-react-layout'
import { Button } from '@progress/kendo-react-buttons'
import { Label, Error } from '@progress/kendo-react-labels'

import {
  CommonActions,
  useAppDispatch,
  useAppSelector
} from '../../../stores'
import { useAccessibleAuthority, usePageTitle } from '../../../components/hooks'
import {
  BranchSelect,
  ExpenseSelect,
  FiscalMonthDropDownList,
  OutputYearsDropDownList,
  DepartmentDropDownList,
  ConfirmDialog,
  Message,
  ErrorMessages,
  ErrorMessage
} from '../../../components/parts'
import { BlobFileService, MessageProvider } from '../../../services'
import { YamadumiManagementGateWay } from '../../../gateway/simulation'
import { GetLaborStackManagementTableFileUrlsReqParams } from '@gateway.Simulation'
import { Authority } from '../../../gateway/system/Authority'

const TITLE = '山積管理表'

const now = dayjs()
const currentYear = now.year()
// Months are zero indexed, so January is month 0.
const currentMonth = now.month() + 1

export const LaborStackManagementTable: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch)

  // #region hooks start
  usePageTitle(TITLE)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)

  const [cardFormInfo, setCardFormInfo] = useState<{
    /** 支店コード 配列 */
    branchCodeList: string[]
    /** フォーマット */
    departmentCode: string
    /** 費目コード 配列 */
    expenseCodeList: string[]
    /** 山積対象年度 */
    targetYear: number
    /** 山積対象月度 */
    targetMonth: number
    /** 出力年数 */
    outputYear: string
  }>({
    branchCodeList: [],
    departmentCode: '1',
    expenseCodeList: [],
    targetYear: currentYear,
    targetMonth: currentMonth,
    outputYear: '3'
  })

  const [isLoading, setIsLoading] = useState(false)
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false)
  const [timeErrorVisibleTargetYM, setTimeErrorVisibleTargetYM] = useState(false)
  const [timeErrorVisibleRequired, setTimeErrorVisibleRequired] = useState(false)
  const [errorMessageId, setErrorMessageId] = useState('')
  const [errorMessageArgs, setErrorMessageArgs] = useState([''])

  // #endregion hooks end

  // #region logic functions start
  /** 山積実施年月度チェック */
  const checkTime = () => {
    let checkResult = true

    const selectYear = cardFormInfo.targetYear
    const selectMonth = cardFormInfo.targetMonth

    // 山積実施年月が当年月より後を選択されている
    if (
      selectYear > currentYear ||
      (selectYear === currentYear && selectMonth > currentMonth)
    ) {
      setTimeErrorVisibleTargetYM(true)

      checkResult = false
    }

    return checkResult
  }

  /** 必須チェック */
  const checkRequired = () => {
    let checkRequiredResult = true
    let checkRequiredMessage = ''

    const appendSeparatorIfNeed = () => {
      if (checkRequiredMessage.length !== 0) {
        checkRequiredMessage += '、'
      }
    }

    if (cardFormInfo.branchCodeList.length === 0) {
      checkRequiredMessage += '支店'
    }
    if (cardFormInfo.expenseCodeList.length === 0) {
      appendSeparatorIfNeed()
      checkRequiredMessage += '費目'
    }
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!cardFormInfo.departmentCode) {
      appendSeparatorIfNeed()
      checkRequiredMessage += 'フォーマット'
    }
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!cardFormInfo.targetYear) {
      appendSeparatorIfNeed()
      checkRequiredMessage += '山積実施年度'
    }
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!cardFormInfo.targetMonth) {
      appendSeparatorIfNeed()
      checkRequiredMessage += '山積実施月度'
    }
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!cardFormInfo.outputYear) {
      appendSeparatorIfNeed()
      checkRequiredMessage += '出力年数'
    }

    if (checkRequiredMessage.length !== 0) {
      checkRequiredResult = false
    }

    return { checkRequiredResult, checkRequiredMessage }
  }

  const checkFormData = () => {
    const { checkRequiredResult, checkRequiredMessage } = checkRequired()
    const checkTimeResult = checkTime()
    return { checkRequiredResult, checkRequiredMessage, checkTimeResult }
  }

  const callApi = () => {
    const separator = '-'
    /** ["YYYY","MM"] */
    const timeList = dayjs()
      // Months are zero indexed, so January is month 0.
      .month(cardFormInfo.targetMonth - 1)
      .year(cardFormInfo.targetYear)
      .format(`YYYY${separator}MM`)
      .split(separator)

    cardFormInfo.expenseCodeList.forEach(expense => {
      const reqParams: GetLaborStackManagementTableFileUrlsReqParams = {
        branchCd: cardFormInfo.branchCodeList,
        format: cardFormInfo.departmentCode,
        expenses: [expense],
        targetYear: timeList[0],
        targetMonth: timeList[1],
        outputYears: cardFormInfo.outputYear,
        displayStartYear
      }

      setIsLoading(true)
      dispatch(CommonActions.setIsLoading(true))
      YamadumiManagementGateWay.getLaborStackManagementTableFileUrls(reqParams)
        .then((res) => {
          if (!res.error) {
            if (Array.isArray(res.data.workingFileNameList)) {
              setNotificationMessages([
                {
                  id: 'I004',
                  content: MessageProvider.provide('I004', '山積管理表出力')
                }
              ])

              res.data.workingFileNameList.forEach((filePath) => {
                if (typeof filePath === 'string') {
                  BlobFileService.downloadRedirectTo(filePath)
                }
              })
            }
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const apiMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })

              setErrorMessages(apiMessages)
            }
          }
        })
        .finally(() => {
          setIsLoading(false)
          dispatch(CommonActions.setIsLoading(false))
        })
    })
  }
  // #endregion logic functions end

  // #region render functions start
  return (
    <>
      <Card style={{ width: '55%', margin: '30px auto' }}>
        <CardHeader style={{ textAlign: 'center' }}>
          <CardTitle>{TITLE}</CardTitle>
        </CardHeader>
        <CardBody>
          <GridLayout cols={[{ width: '30%' }]} gap={{ cols: 5, rows: 5 }}>
            <GridLayoutItem row={1} col={1}>
              <Label>フォーマット</Label>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={2} colSpan={2}>
              <GridLayoutItem row={1} col={2}>
                <DepartmentDropDownList
                  value={cardFormInfo.departmentCode}
                  onChange={(value: string) => {
                    setCardFormInfo(
                      produce((draft) => {
                        draft.departmentCode = value
                      })
                    )
                  }}
                />
              </GridLayoutItem>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={1}>
              <Label>支店</Label>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={2} colSpan={2}>
              <BranchSelect
                multiple
                value={cardFormInfo.branchCodeList}
                onChange={(valueList: string[]) => {
                  setCardFormInfo(
                    produce((draft) => {
                      draft.branchCodeList = valueList
                    })
                  )
                }}
                autoClose={false}
              />
            </GridLayoutItem>
            <GridLayoutItem row={3} col={1}>
              <Label>費目</Label>
            </GridLayoutItem>
            <GridLayoutItem row={3} col={2} colSpan={2}>
              <GridLayoutItem row={1} col={2}>
                <ExpenseSelect
                  multiple
                  value={cardFormInfo.expenseCodeList}
                  onChange={(valueList) => {
                    setCardFormInfo(
                      produce((draft) => {
                        draft.expenseCodeList = valueList
                      })
                    )
                  }}
                  autoClose={false}
                />
              </GridLayoutItem>
            </GridLayoutItem>
            <GridLayoutItem row={4} col={1}>
              <Label>山積実施年月度</Label>
            </GridLayoutItem>
            <GridLayoutItem row={4} col={2} colSpan={2}>
              <FiscalMonthDropDownList
                startYear={2018}
                fyddlValue={cardFormInfo.targetYear}
                onYearChange={(year) => {
                  if (timeErrorVisibleTargetYM) {
                    setTimeErrorVisibleTargetYM(false)
                  }

                  setCardFormInfo(
                    produce((draft) => {
                      draft.targetYear = year
                    })
                  )
                }}
                fmddlValue={cardFormInfo.targetMonth}
                onMonthChange={(month) => {
                  if (timeErrorVisibleTargetYM) {
                    setTimeErrorVisibleTargetYM(false)
                  }

                  setCardFormInfo(
                    produce((draft) => {
                      draft.targetMonth = month
                    })
                  )
                }}
              />
              {timeErrorVisibleTargetYM && (
                <Error>
                  <Message id={'E014'} args={['山積実施年月度', '当月以前']} />
                </Error>
              )}
            </GridLayoutItem>
            <GridLayoutItem row={5} col={1}>
              <Label>出力年数</Label>
            </GridLayoutItem>
            <GridLayoutItem row={5} col={2} colSpan={2}>
              <GridLayoutItem row={1} col={2}>
                <OutputYearsDropDownList
                  value={cardFormInfo.outputYear}
                  onChange={(value) => {
                    setCardFormInfo(
                      produce((draft) => {
                        draft.outputYear = value
                      })
                    )
                  }}
                />
              </GridLayoutItem>
            </GridLayoutItem>
            <GridLayoutItem row={6} col={2} colSpan={2}>
              {timeErrorVisibleRequired && (
                  <Error>
                    <Message id={errorMessageId} args={errorMessageArgs} />
                  </Error>
              )}
            </GridLayoutItem>
          </GridLayout>
        </CardBody>
        <CardActions layout="end">
          <StackLayout gap={5}>
            <Button
              style={{ minWidth: 120 }}
              onClick={() => {
                navigate(-1)
              }}
            >
              戻る
            </Button>
            <Button
              style={{ minWidth: 120 }}
              themeColor="primary"
              disabled={isLoading}
              onClick={() => {
                setTimeErrorVisibleTargetYM(false)
                setTimeErrorVisibleRequired(false)
                setConfirmDialogVisible(true)
              }}
            >
              出力
            </Button>
          </StackLayout>
        </CardActions>
      </Card>

      <ConfirmDialog
        open={confirmDialogVisible}
        renderCloseButton
        title={'確認'}
        id={'I002'}
        args={['山積管理表', '出力開始']}
        onClickOk={() => {
          setConfirmDialogVisible(false)

          const { checkRequiredResult, checkRequiredMessage, checkTimeResult } = checkFormData()

          if (checkRequiredResult && checkTimeResult) {
            callApi()
          } else {
            if (!checkRequiredResult && checkRequiredMessage !== '') {
              setErrorMessageId('E001')
              setErrorMessageArgs([checkRequiredMessage, '山積管理表の出力'])
              setTimeErrorVisibleRequired(true)
            }
            if (!checkTimeResult) {
              setTimeErrorVisibleTargetYM(true)
            }
          }
        }}
        onClickCancel={() => {
          setConfirmDialogVisible(false)
        }}
      />
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
  // #endregion render functions end
}
